import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Truck, MapPin, DollarSign, ArrowRight, Clock, Tag, AlertTriangle, Loader2, CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResumePaymentPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [errorState, setErrorState] = useState(null); // 'expired' | 'paid' | 'not_found'

  const fetchQuote = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/resume/${token}`);
      if (res.status === 410) { setErrorState('expired'); return; }
      if (res.status === 409) { setErrorState('paid'); return; }
      if (!res.ok) { setErrorState('not_found'); return; }
      const data = await res.json();
      setQuote(data);
    } catch {
      setErrorState('not_found');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const handlePayDeposit = async () => {
    setPaymentLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/resume/${token}/checkout`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to create payment session');
      }
      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error(error.message);
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (errorState === 'expired') {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-24 pb-20 flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
            <h1 className="font-outfit text-3xl font-semibold mb-4">Payment Link Expired</h1>
            <p className="text-muted-foreground mb-8">
              This quote link has expired. Get a fresh quote in seconds -- your Tesla deserves a smooth journey.
            </p>
            <Button onClick={() => navigate('/quote')} className="rounded-full px-8 py-6 hover:scale-105 active:scale-95 transition-transform">
              Get a New Quote
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (errorState === 'paid') {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-24 pb-20 flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6">
              <CheckCircle className="h-8 w-8 text-accent" />
            </div>
            <h1 className="font-outfit text-3xl font-semibold mb-4">Already Paid</h1>
            <p className="text-muted-foreground mb-8">
              Your deposit has already been received. Our team is on it -- you'll receive updates shortly.
            </p>
            <Button onClick={() => navigate('/')} className="rounded-full px-8 py-6 hover:scale-105 active:scale-95 transition-transform">
              Go Home
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (errorState === 'not_found' || !quote) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-24 pb-20 flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-6">
              <AlertTriangle className="h-8 w-8 text-muted-foreground" />
            </div>
            <h1 className="font-outfit text-3xl font-semibold mb-4">Link Not Found</h1>
            <p className="text-muted-foreground mb-8">
              This payment link is invalid or has already been used. Start a new quote below.
            </p>
            <Button onClick={() => navigate('/quote')} className="rounded-full px-8 py-6 hover:scale-105 active:scale-95 transition-transform">
              Get a New Quote
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const { vehicle, route, pricing, customer_name, token_expires_at, reminder_number } = quote;
  const isDiscounted = pricing.discount_percent > 0;

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-4xl mx-auto px-6">

          {/* Header */}
          <div className="text-center mb-12 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-accent/10 mb-6">
              <DollarSign className="h-8 w-8 text-accent" />
            </div>
            {isDiscounted ? (
              <>
                <div className="inline-flex items-center gap-2 bg-accent/10 text-accent text-sm font-semibold px-4 py-2 rounded-full mb-4">
                  <Tag className="h-4 w-4" />
                  {pricing.discount_percent}% Discount Applied
                </div>
                <h1 className="font-outfit text-4xl font-semibold tracking-tight mb-4">
                  Special Offer -- Complete Your Booking
                </h1>
              </>
            ) : (
              <h1 className="font-outfit text-4xl font-semibold tracking-tight mb-4">
                Complete Your Tesla Shipping
              </h1>
            )}
            <p className="text-muted-foreground">
              {customer_name ? `Hi ${customer_name}, your` : 'Your'} quote is still reserved. Secure your spot with a deposit below.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Quote Details Card */}
            <div className="lg:col-span-2">
              <Card className="glass border-border">
                <CardHeader>
                  <CardTitle className="font-outfit flex items-center justify-between">
                    <span>Transport Details</span>
                    <Badge variant={vehicle.transport_type === 'enclosed' ? 'default' : 'secondary'}>
                      {vehicle.transport_type === 'enclosed' ? 'Enclosed' : 'Open'} Carrier
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Vehicle */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <Truck className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Vehicle</p>
                      <p className="font-medium">
                        {vehicle.year} {vehicle.make} {vehicle.model}
                      </p>
                      <p className="text-sm text-muted-foreground capitalize">{vehicle.condition} condition</p>
                    </div>
                  </div>

                  {/* Route */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <MapPin className="h-5 w-5 text-accent" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground">Route</p>
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{route.pickup_zip}</span>
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{route.delivery_zip}</span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{route.distance_miles} miles</p>
                    </div>
                  </div>

                  {/* Link expiry */}
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                      <Clock className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Offer Expires</p>
                      <p className="font-medium text-sm">{token_expires_at}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Payment Card */}
            <div className="space-y-4">
              <Card className="glass border-border">
                <CardHeader>
                  <CardTitle className="font-outfit text-lg">Payment Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Estimated transport</span>
                    <span className="font-medium">${pricing.customer_price?.toLocaleString()}</span>
                  </div>

                  <div className="border-t border-border pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground text-sm">Deposit due now</span>
                      <div className="text-right">
                        {isDiscounted ? (
                          <>
                            <p className="text-xs text-muted-foreground line-through">
                              ${pricing.deposit_amount.toFixed(2)}
                            </p>
                            <p className="text-xl font-semibold font-outfit text-accent">
                              ${pricing.discounted_deposit.toFixed(2)}
                            </p>
                            <Badge className="text-xs bg-accent/20 text-accent border-0 mt-1">
                              Save {pricing.discount_percent}%
                            </Badge>
                          </>
                        ) : (
                          <p className="text-xl font-semibold font-outfit">
                            ${pricing.deposit_amount.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Deposit is applied toward your transport total. Fully refundable before dispatch.
                  </p>

                  <Button
                    onClick={handlePayDeposit}
                    disabled={paymentLoading}
                    className="w-full rounded-full py-6 hover:scale-105 active:scale-95 transition-transform font-semibold"
                  >
                    {paymentLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : null}
                    {isDiscounted
                      ? `Pay $${pricing.discounted_deposit.toFixed(2)} -- Save ${pricing.discount_percent}%`
                      : `Pay $${pricing.deposit_amount.toFixed(2)} Deposit`}
                  </Button>

                  <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                    <span>ðŸ"’ Secured by Stripe</span>
                  </div>
                </CardContent>
              </Card>

              {reminder_number >= 2 && (
                <Card className="border border-accent/20 bg-accent/5">
                  <CardContent className="pt-4 pb-4">
                    <p className="text-xs text-muted-foreground text-center">
                      {reminder_number === 3
                        ? 'This is your final reminder. Your discounted offer expires soon.'
                        : 'Prices are subject to change. Complete your booking to lock in this rate.'}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

